import type { components } from "@qorakul/shared/api";
import type { AcademicYear } from "./classes.ts";

export type RolloverPreview = components["schemas"]["RolloverPreview"];
export type RolloverRow = components["schemas"]["RolloverPreviewRow"];
export type RolloverClassOption = components["schemas"]["RolloverClassOption"];
export type RolloverResult = components["schemas"]["RolloverResult"];
export type RolloverOutcome = NonNullable<components["schemas"]["RolloverOutcome"]>;
export type RolloverDecisionInput = components["schemas"]["RolloverDecision"];

/** What the user picked for one student; "Skip" leaves the student out of the confirmed list. */
export type RolloverChoice = { outcome: RolloverOutcome | "Skip"; targetClassId: string | undefined };

/** The proposal as the starting choice; students already placed in the new year are skipped. */
export function initialChoice(row: RolloverRow): RolloverChoice {
  if (row.note === "AlreadyPlaced" || !row.proposedOutcome) return { outcome: "Skip", targetClassId: undefined };
  return { outcome: row.proposedOutcome, targetClassId: row.proposedTargetClassId ?? undefined };
}

/** Target classes a choice may use: the next grade to promote, the same grade to repeat. */
export function targetOptions(row: RolloverRow, outcome: RolloverChoice["outcome"], classes: RolloverClassOption[]): RolloverClassOption[] {
  const grade = Number(row.sourceGrade);
  if (outcome === "Promote") return classes.filter((c) => Number(c.grade) === grade + 1);
  if (outcome === "Repeat") return classes.filter((c) => Number(c.grade) === grade);
  return [];
}

export function needsTarget(choice: RolloverChoice): boolean {
  return (choice.outcome === "Promote" || choice.outcome === "Repeat") && !choice.targetClassId;
}

/** The decisions sent to the API, in table order; skipped students are not sent. */
export function toDecisions(rows: RolloverRow[], choices: Record<string, RolloverChoice>): RolloverDecisionInput[] {
  return rows.flatMap((row) => {
    const choice = choices[row.studentId];
    if (!choice || choice.outcome === "Skip") return [];
    return [{ studentId: row.studentId, outcome: choice.outcome, targetClassId: choice.outcome === "Graduate" ? null : (choice.targetClassId ?? null) }];
  });
}

/** Default source: the active year; default target: the first later year that is not closed. */
export function defaultYears(years: AcademicYear[]): { source: string | undefined; target: string | undefined } {
  const sorted = [...years].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const source = sorted.find((y) => y.status === "Active") ?? sorted.at(-2);
  const target = source ? sorted.find((y) => y.startDate > source.endDate && y.status !== "Closed") : undefined;
  return { source: source?.id, target: target?.id };
}
